import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, query, where, onSnapshot, doc, updateDoc, setDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from './ThemeProvider';
import { useLanguage } from './LanguageProvider';
import LanguageSwitcher from './LanguageSwitcher';
import { AnimatedThemeToggler } from './AnimatedThemeToggler';
import ShimmerButton from './ShimmerButton';
import { useGamerAuth } from './GamerAuthContext';
import { IoNotifications, IoCheckmarkCircle, IoBagCheck, IoPersonAdd, IoChatbubbleEllipses, IoTrophy, IoTrashOutline } from 'react-icons/io5';

function ModeSwitcher({ isGameswap }) {
    const [showAuth, setShowAuth] = useState(false);
    const [pin, setPin] = useState('');
    const [error, setError] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    
    const { gamerUser, loginWithCode } = useGamerAuth();

    const executeToggle = (targetGameswap) => {
        const newPath = targetGameswap ? '/gameswap' : '/';
        window.history.pushState({}, '', newPath);
        window.dispatchEvent(new CustomEvent('gameswapToggle', { detail: targetGameswap }));
        // Scroll to top smoothly with a slight delay so DOM has time to update
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 50);
    };

    const handleToggle = (e, targetGameswap) => {
        e.preventDefault();
        if (targetGameswap && !isGameswap) {
            if (gamerUser) {
                executeToggle(true);
            } else {
                setShowAuth(true);
            }
        } else {
            executeToggle(targetGameswap);
        }
    };

    const handlePinSubmit = async (e) => {
        e.preventDefault();
        if (!pin.trim()) return;

        setIsChecking(true);
        setError(false);
        try {
            await loginWithCode(pin.trim().toUpperCase());
            setShowAuth(false);
            setPin('');
            setError(false);
            executeToggle(true);
        } catch (err) {
            console.error(err);
            setError(true);
            setTimeout(() => setError(false), 2000);
        } finally {
            setIsChecking(false);
        }
    };

    return (
        <>
            <ShimmerButton 
                asDiv 
                className="!p-1 !h-auto w-[150px]" 
                background={isGameswap ? '#0a0a0a' : '#FF9500'} 
                shimmerColor={isGameswap ? '#A855F7' : '#ffffff'}
            >
                <div className="flex z-10 gap-1 w-full h-full">
                    <a href="/" onClick={(e) => handleToggle(e, false)} className={`flex-1 flex items-center justify-center py-1.5 rounded-md text-sm font-bold transition-all duration-300 ${!isGameswap ? 'bg-white text-black shadow-md' : 'text-gray-400 hover:text-white'}`}>BGMI</a>
                    <button disabled className={`flex-1 flex items-center justify-center py-1.5 rounded-md text-sm font-bold transition-all duration-300 opacity-50 cursor-not-allowed ${isGameswap ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-[0_0_15px_rgba(219,39,119,0.5)]' : 'text-black/80'}`} title="GAMER mode is temporarily disabled">GAMER</button>
                </div>
            </ShimmerButton>

            {typeof window !== 'undefined' ? createPortal(
                <AnimatePresence>
                    {showAuth && (
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/80 backdrop-blur-md"
                        >
                            <motion.div 
                                initial={{ scale: 0.9, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.9, y: 20 }}
                                className="bg-[#111111] border border-[#333333] p-8 rounded-2xl shadow-2xl max-w-sm w-full mx-4"
                            >
                                <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 mb-2 text-center tracking-wider">GAMER LOGIN</h3>
                                <p className="text-sm text-gray-400 text-center mb-6">Enter your GAMER Code to unlock GAMER mode.</p>
                                
                                <form onSubmit={handlePinSubmit}>
                                    <input 
                                        type="text" 
                                        autoFocus
                                        value={pin}
                                        onChange={(e) => setPin(e.target.value.toUpperCase())}
                                        placeholder="GAMER CODE"
                                        className={`w-full bg-black border ${error ? 'border-red-500 text-red-500' : 'border-[#333] text-white focus:border-pink-500'} rounded-lg px-4 py-3 text-center text-xl tracking-widest outline-none transition-colors uppercase`}
                                    />
                                    {error && <p className="text-red-500 text-xs text-center mt-2">Login Failed.</p>}
                                    <div className="flex gap-3 mt-6">
                                        <button type="button" onClick={() => { setShowAuth(false); setPin(''); setError(false); }} className="flex-1 py-3 rounded-lg bg-[#222] text-white font-medium hover:bg-[#333] transition-colors">Cancel</button>
                                        <button type="submit" disabled={isChecking} className="flex-1 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:opacity-90 transition-opacity shadow-[0_0_15px_rgba(219,39,119,0.3)] disabled:opacity-50">
                                            {isChecking ? 'Checking...' : 'Enter'}
                                        </button>
                                    </div>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            ) : null}
        </>
    );
}

export default function Header() {
    const { gamerUser, loginWithCode, loginWithGoogle } = useGamerAuth();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [headerToast, setHeaderToast] = useState(null);
    const { t } = useLanguage();

    useEffect(() => {
        if (headerToast) {
            const timer = setTimeout(() => setHeaderToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [headerToast]);

    useEffect(() => {
        const handleAuthToast = () => setHeaderToast("PLEASE LOGIN TO YOUR ACCOUNT");
        window.addEventListener('showAuthToast', handleAuthToast);
        return () => window.removeEventListener('showAuthToast', handleAuthToast);
    }, []);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const [currentHash, setCurrentHash] = useState(typeof window !== 'undefined' ? window.location.hash || '#' : '#');
    const [searchUid, setSearchUid] = useState('');

    useEffect(() => {
        const handleHashChange = () => {
            setCurrentHash(window.location.hash);
            setOpen(false);
            if (window.location.hash !== '#find-teammates' && window.location.hash !== '#bgmi-accounts') {
                setSearchUid('');
                window.dispatchEvent(new CustomEvent('teamSearch', { detail: '' }));
            }
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, []);

    const handleSearchChange = (e) => {
        setSearchUid(e.target.value);
        window.dispatchEvent(new CustomEvent('teamSearch', { detail: e.target.value }));
    };

    const { theme, setTheme } = useTheme();
    const [isGameswap, setIsGameswap] = useState(window.location.pathname.includes('gameswap'));

    useEffect(() => {
        const handleToggle = (e) => setIsGameswap(e.detail);
        window.addEventListener('gameswapToggle', handleToggle);
        return () => window.removeEventListener('gameswapToggle', handleToggle);
    }, []);

    useEffect(() => {
        if (isGameswap && setTheme) {
            setTheme('dark');
        }
    }, [isGameswap, setTheme]);

    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);

    useEffect(() => {
        if (showNotifications) {
            window.history.pushState({ popupOpen: true }, '');

            const handlePopState = (e) => {
                setShowNotifications(false);
            };

            window.addEventListener('popstate', handlePopState);
            
            return () => {
                window.removeEventListener('popstate', handlePopState);
                if (window.history.state && window.history.state.popupOpen) {
                    window.history.back();
                }
            };
        }
    }, [showNotifications]);

    const [realNotifications, setRealNotifications] = useState([]);

    useEffect(() => {
        if (!gamerUser) {
            setRealNotifications([]);
            return;
        }
        const userId = gamerUser.uid || gamerUser.gamerCode;
        if (!userId) return;
                // Incoming requests
        const q1 = query(collection(db, 'teamRequests'), where('receiverId', '==', userId), where('status', '==', 'pending'));
        const unsub1 = onSnapshot(q1, (snap) => {
            const incoming = [];
            snap.forEach(d => incoming.push({ id: d.id, ...d.data(), type: 'incoming' }));
            setRealNotifications(prev => {
                const others = prev.filter(p => p.type !== 'incoming');
                return [...others, ...incoming].sort((a, b) => b.createdAt - a.createdAt);
            });
        });

        // Accepted requests (where I am the sender)
        const q2 = query(collection(db, 'teamRequests'), where('senderId', '==', userId), where('status', '==', 'accepted'));
        const unsub2 = onSnapshot(q2, (snap) => {
            const accepted = [];
            snap.forEach(d => accepted.push({ id: d.id, ...d.data(), type: 'friend' }));
            setRealNotifications(prev => {
                const others = prev.filter(p => p.type !== 'friend' || p.receiverId === userId);
                return [...others, ...accepted].sort((a, b) => b.createdAt - a.createdAt);
            });
        });

        // Accepted requests (where I am the receiver)
        const q3 = query(collection(db, 'teamRequests'), where('receiverId', '==', userId), where('status', '==', 'accepted'));
        const unsub3 = onSnapshot(q3, (snap) => {
            const accepted = [];
            snap.forEach(d => accepted.push({ id: d.id, ...d.data(), type: 'friend' }));
            setRealNotifications(prev => {
                const others = prev.filter(p => p.type !== 'friend' || p.senderId === userId);
                return [...others, ...accepted].sort((a, b) => b.createdAt - a.createdAt);
            });
        });

        return () => { unsub1(); unsub2(); unsub3(); };
    }, [gamerUser]);

    const handleAcceptRequest = async (req) => {
        try {
            await updateDoc(doc(db, 'teamRequests', req.id), { status: 'accepted' });
            
            if (req.requestType === 'team') {
                const currentUserId = String(gamerUser?.uid || gamerUser?.gamerCode);
                const profile = gamerUser?.profileData || {};
                const pSlot = profile.primaryUidSlot || 1;
                const myGameUid = String(pSlot === 1 ? profile.gameUid1 : profile.gameUid2);
                const teamRef = collection(db, 'findTeammates');
                
                // My Team Slots (Recruiter)
                const lookingFor = req.gamerData?.lookingFor || 'DUO';
                const slotPrefix = lookingFor === 'SQUAD' ? 'squad-' : 'duo-';
                const maxSlots = lookingFor === 'SQUAD' ? 4 : 2;

                const myTeamSnap = await getDocs(query(teamRef, where('ownerId', '==', currentUserId)));
                const mySlots = [];
                let hasDuplicate = false;
                myTeamSnap.forEach(d => {
                    if (d.id.startsWith('TM_') && d.data().slotId?.startsWith(slotPrefix)) {
                        mySlots.push(d.data().slotId);
                        if (String(d.data().uid) === String(req.senderGameUid || req.senderId)) hasDuplicate = true;
                    }
                });

                if (!hasDuplicate && mySlots.length < maxSlots) {
                    let slotToFill = `${slotPrefix}1`;
                    for (let i = 1; i <= maxSlots; i++) {
                        if (!mySlots.includes(`${slotPrefix}${i}`)) {
                            slotToFill = `${slotPrefix}${i}`;
                            break;
                        }
                    }
                    await setDoc(doc(db, 'findTeammates', `TM_${Date.now()}`), {
                        slotId: slotToFill,
                        name: req.senderName || 'Unknown',
                        uid: req.senderGameUid || req.senderId,
                        role: req.senderRole || 'ASSAULTER',
                        kd: req.senderKd || '3.0',
                        avatar: req.senderAvatar || '',
                        ownerId: currentUserId,
                        createdAt: Date.now()
                    });
                    
                    if (mySlots.length === maxSlots - 1) { // Will become full
                        myTeamSnap.forEach(async (d) => {
                            if (!d.id.startsWith('TM_')) await deleteDoc(d.ref); // Disable LFT
                        });
                    }
                }

                // Their Team Slots (Sender)
                const theirTeamSnap = await getDocs(query(teamRef, where('ownerId', '==', req.senderId)));
                const theirSlots = [];
                let theyHaveDuplicate = false;
                theirTeamSnap.forEach(d => {
                    if (d.id.startsWith('TM_') && d.data().slotId?.startsWith(slotPrefix)) {
                        theirSlots.push(d.data().slotId);
                        if (String(d.data().uid) === myGameUid) theyHaveDuplicate = true;
                    }
                });

                if (!theyHaveDuplicate && theirSlots.length < maxSlots && myGameUid && myGameUid !== 'undefined') {
                    let theirSlotToFill = `${slotPrefix}1`;
                    for (let i = 1; i <= maxSlots; i++) {
                        if (!theirSlots.includes(`${slotPrefix}${i}`)) {
                            theirSlotToFill = `${slotPrefix}${i}`;
                            break;
                        }
                    }
                    await setDoc(doc(db, 'findTeammates', `TM_${Date.now() + 1}`), {
                        slotId: theirSlotToFill,
                        name: profile.name || gamerUser?.name || 'Unknown',
                        uid: myGameUid,
                        role: profile.role || 'ASSAULTER',
                        kd: profile.kd || '3.0',
                        avatar: gamerUser?.photoURL || profile.avatar || '',
                        ownerId: req.senderId,
                        createdAt: Date.now()
                    });
                }
            }
        } catch (e) {
            console.error("Error accepting request:", e);
        }
    };

    const handleRejectRequest = async (req) => {
        try {
            await deleteDoc(doc(db, 'teamRequests', req.id));
        } catch (e) {
            console.error("Error rejecting request:", e);
        }
    };
    
    const dismissNotification = async (reqId) => {
        try {
            await deleteDoc(doc(db, 'teamRequests', reqId));
        } catch (e) {
            console.error("Error dismissing notification:", e);
        }
    };

    const [hasViewedNotifications, setHasViewedNotifications] = useState(false);
    const [prevNotifCount, setPrevNotifCount] = useState(0);

    useEffect(() => {
        const currentCount = realNotifications.length;
        if (currentCount > prevNotifCount) {
            setHasViewedNotifications(false);
        }
        setPrevNotifCount(currentCount);
    }, [realNotifications.length, prevNotifCount]);

    const hasNotifications = realNotifications.length > 0 && !hasViewedNotifications;

    const handleClearAllNotifications = async () => {
        setHasViewedNotifications(true);
        if (!gamerUser) return;
        const userId = gamerUser.uid || gamerUser.gamerCode;
        try {
            const notifRef = collection(db, 'users', userId, 'notifications');
            const snap = await getDocs(notifRef);
            snap.forEach((d) => {
                deleteDoc(doc(db, 'users', userId, 'notifications', d.id));
            });
            const incomingRequests = realNotifications.filter(n => n.type === 'incoming');
            incomingRequests.forEach(req => {
                deleteDoc(doc(db, 'teamRequests', req.id));
            });
        } catch (e) {
            console.error("Error clearing notifications from firestore:", e);
        }
    };
    
    const [loginPin, setLoginPin] = useState('');
    const [loginError, setLoginError] = useState(false);
    const [isLoginChecking, setIsLoginChecking] = useState(false);


    const handleGoogleLoginClick = async () => {
        try {
            await loginWithGoogle();
            window.location.hash = '#profile';
        } catch (err) {
            console.error("Google login failed", err);
        }
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        if (!loginPin.trim()) return;

        setIsLoginChecking(true);
        setLoginError(false);
        try {
            await loginWithCode(loginPin.trim().toUpperCase());
            setShowLoginModal(false);
            setLoginPin('');
            setLoginError(false);
            window.location.hash = '#profile';
        } catch (err) {
            console.error(err);
            setLoginError(true);
            setTimeout(() => setLoginError(false), 2000);
        } finally {
            setIsLoginChecking(false);
        }
    };

    return (
        <>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 top-16 bg-black/60 backdrop-blur-sm z-[40] md:hidden"
                        onClick={() => setOpen(false)}
                    />
                )}
            </AnimatePresence>
            <motion.header
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className={`fixed w-full top-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? `backdrop-blur-md bg-white/85 dark:bg-[rgba(10,10,10,0.85)] ${!isGameswap ? 'border-b border-gray-200 dark:border-[#1f1f1f]' : ''}`
                        : 'bg-transparent'
                }`}
                role="banner"
            >
                <div className="mx-auto max-w-[120rem] px-[15px]">
                    <div className="flex h-16 items-center justify-between relative">
                        {/* Brand */}
                        <a href="/" className="flex items-center gap-2" aria-label="GridLabs Production home">
                            <img
                                src="/gridlabsprodLogo/darkLogo.png"
                                alt="GridLabs Production Logo"
                                width={160}
                                height={48}
                                className="h-10 w-auto"
                            />
                        </a>

                        {/* Desktop Navigation */}
                        <nav className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-6">
                            {['MAIN LOBBY', 'RECRUIT ZONE'].map((tab) => (
                                <a 
                                    key={tab} 
                                    href={`#${tab.toLowerCase().replace(/ /g, '-')}`}
                                    onClick={(e) => {
                                        if (tab !== 'MAIN LOBBY' && !gamerUser) {
                                            e.preventDefault();
                                            setHeaderToast("PLEASE LOGIN TO YOUR ACCOUNT");
                                        }
                                    }}
                                    className="relative text-xl font-bold font-['Iceland'] text-gray-700 hover:text-pink-600 transition-colors group tracking-wider uppercase"
                                >
                                    {tab}
                                    <span className="absolute left-0 bottom-[-4px] w-0 h-[2px] bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-300 ease-out group-hover:w-full"></span>
                                </a>
                            ))}
                        </nav>

                        {/* Desktop CTA */}
                        <div className="hidden md:flex items-center gap-3">
                            {(currentHash === '#find-teammates' || currentHash === '#bgmi-accounts') && (
                                <div className="relative mr-2">
                                    <input 
                                        type="text" 
                                        placeholder={currentHash === '#bgmi-accounts' ? "Search Account..." : "Search GAME UID..."}
                                        value={searchUid}
                                        onChange={handleSearchChange}
                                        className="bg-gray-100 dark:bg-[#1a1a24] border border-gray-200 dark:border-white/10 rounded-[50px] px-5 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-pink-500 w-56 transition-all font-mono shadow-sm"
                                    />
                                    <svg className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                                </div>
                            )}
                            {!isGameswap && (
                                <>
                                    <AnimatedThemeToggler />
                                    <button onClick={(e) => {
                                        if (!gamerUser) {
                                            e.preventDefault();
                                            setHeaderToast("PLEASE LOGIN TO YOUR ACCOUNT");
                                            return;
                                        }
                                        setShowNotifications(!showNotifications); 
                                        if (!showNotifications) setHasViewedNotifications(true); 
                                    }} className="relative flex items-center justify-center w-9 h-9 rounded-xl md:rounded-full border border-gray-200 dark:border-white/10 hover:border-pink-500/50 bg-gray-50 dark:bg-[#1a1a24] text-gray-600 dark:text-gray-300 transition-all duration-300 shadow-sm group">
                                        <IoNotifications className={`w-4 h-4 group-hover:text-pink-500 transition-colors ${hasNotifications ? 'text-pink-500 animate-pulse' : ''}`} />
                                        {hasNotifications && (
                                            <span className="absolute top-[8px] right-[8px] flex h-2 w-2">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500"></span>
                                            </span>
                                        )}
                                    </button>
                                </>
                            )}
                            {gamerUser ? (
                                <a href="#profile" className="flex items-center justify-center w-9 h-9 rounded-xl md:rounded-full border border-gray-200 dark:border-white/10 hover:border-pink-500/50 bg-gray-50 dark:bg-[#1a1a24] transition-all duration-300 overflow-hidden group shadow-sm">
                                    <img src={gamerUser.photoURL || "https://i.pravatar.cc/150?img=33"} alt="Profile" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                                </a>
                            ) : (
                                <ShimmerButton 
                                    onClick={handleGoogleLoginClick}
                                    className="!p-[2px] !h-[42px] w-[90px]" 
                                    background={isGameswap ? '#0a0a0a' : '#FF9500'} 
                                    shimmerColor={isGameswap ? '#A855F7' : '#ffffff'}
                                >
                                    <div className="flex z-10 w-full h-full pointer-events-none">
                                        <div className={`flex-1 flex items-center justify-center py-0.5 rounded-md text-xl font-['Iceland'] font-bold transition-all duration-300 uppercase tracking-wider ${!isGameswap ? 'bg-transparent text-white' : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-[0_0_15px_rgba(219,39,119,0.5)]'}`}>
                                            LOGIN
                                        </div>
                                    </div>
                                </ShimmerButton>
                            )}
                        </div>

                        {/* Mobile Toggle & Actions */}
                        <div className="flex md:hidden items-center gap-2">
                            {gamerUser && !isGameswap && (
                                <button onClick={(e) => {
                                    setShowNotifications(!showNotifications); 
                                    if (!showNotifications) setHasViewedNotifications(true); 
                                }} className="relative flex items-center justify-center h-10 w-10 rounded-lg border border-gray-300 dark:border-[#1f1f1f] bg-transparent text-gray-600 dark:text-gray-300 hover:text-pink-500 transition-colors shrink-0 group">
                                    <IoNotifications className={`w-5 h-5 ${hasNotifications ? 'text-pink-500 animate-pulse' : ''}`} />
                                    {hasNotifications && (
                                        <span className="absolute top-[8px] right-[8px] flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-pink-500"></span>
                                        </span>
                                    )}
                                </button>
                            )}
                            <MobileToggle open={open} setOpen={setOpen} />
                        </div>
                    </div>
                </div>

                <AnimatePresence>
                    {headerToast && (
                        <motion.div
                            initial={{ opacity: 0, y: "-100%", scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: "-100%", scale: 0.9 }}
                            className="fixed top-4 md:top-6 left-1/2 -translate-x-1/2 z-[999999] bg-red-500 text-white px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-black tracking-widest text-[10px] sm:text-sm uppercase shadow-[0_0_20px_rgba(239,68,68,0.5)] border border-red-400 whitespace-nowrap w-max max-w-[90vw]"
                        >
                            {headerToast}
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Mobile Menu */}
                <MobileMenu open={open} setOpen={setOpen} onLoginClick={handleGoogleLoginClick} currentHash={currentHash} gamerUser={gamerUser} showNotifications={showNotifications} onToggleNotifications={() => { if(!gamerUser) { setHeaderToast("PLEASE LOGIN TO YOUR ACCOUNT"); return; } setShowNotifications(!showNotifications); if (!showNotifications) setHasViewedNotifications(true); }} hasNotifications={hasNotifications} />
                
                {typeof window !== 'undefined' ? createPortal(
                    <AnimatePresence>
                        {showNotifications && (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 z-[999999] flex items-end md:items-center justify-center p-0 md:p-4 bg-black/80 backdrop-blur-md"
                                onClick={() => setShowNotifications(false)}
                            >
                                <motion.div
                                    initial={{ opacity: 0, y: "100%" }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: "100%" }}
                                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                    className="bg-white dark:bg-[#1a1a24] p-[20px] md:p-[15px] rounded-t-3xl md:rounded-xl md:rounded-b-xl border-t border-gray-200 dark:border-white/10 md:border shadow-[0_-10px_40px_rgba(0,0,0,0.3)] md:shadow-2xl w-full max-w-full md:max-w-md font-['Monda'] uppercase relative pb-10 md:pb-[15px]"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Mobile Drag Handle */}
                                    <div className="w-12 h-1.5 bg-gray-300 dark:bg-white/20 rounded-full mx-auto mb-4 md:hidden shrink-0"></div>
                                    <div className="flex items-center justify-between border-b border-gray-200 dark:border-white/10 pb-[10px] mb-[10px]">
                                        <div className="flex gap-4 items-center">
                                            <span className="font-['Monda'] text-sm font-black uppercase tracking-wider transition-colors text-pink-500">
                                                NOTIFICATIONS
                                            </span>
                                        </div>
                                        <div className="flex gap-4 items-center">
                                            <button onClick={handleClearAllNotifications} className="text-gray-400 hover:text-red-500 transition-colors shrink-0" title="Clear All">
                                                <IoTrashOutline className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex flex-col max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar -mx-2 px-2">
                                        {realNotifications.length === 0 ? (
                                            <div className="py-10 text-center">
                                                <p className="text-gray-500 dark:text-gray-400 text-sm font-['Monda'] font-bold uppercase tracking-wider">NO NEW NOTIFICATIONS.</p>
                                            </div>
                                        ) : (
                                            realNotifications.map((notification, index) => {
                                                if (notification.type === 'incoming') {
                                                    return (
                                                        <div key={`real-${notification.id}`} className="flex flex-col gap-2 py-[15px] border-b border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 px-2 transition-colors rounded-lg">
                                                            <div className="flex items-start gap-3">
                                                                <div className="mt-0.5 shrink-0">
                                                                    <IoPersonAdd className="w-5 h-5 text-blue-500" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center justify-between mb-1">
                                                                        <h4 className="font-['Monda'] font-bold text-sm tracking-wider text-gray-900 dark:text-white leading-tight uppercase truncate">{notification.requestType === 'friend' ? 'FRIEND REQUEST' : 'TEAMMATE REQUEST'}</h4>
                                                                        <span className="font-['Monda'] text-[10px] font-black text-pink-500 whitespace-nowrap ml-2 uppercase tracking-wider shrink-0">JUST NOW</span>
                                                                    </div>
                                                                    <p className="font-['Monda'] text-xs text-gray-500 dark:text-gray-400 leading-snug uppercase tracking-wide truncate">{notification.senderName} SENT YOU A REQUEST.</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-2 mt-2 ml-8">
                                                                <button onClick={() => handleAcceptRequest(notification)} className="px-4 py-1.5 bg-green-500 hover:bg-green-600 text-white text-[10px] font-black tracking-widest uppercase rounded">ACCEPT</button>
                                                                <button onClick={() => handleRejectRequest(notification)} className="px-4 py-1.5 bg-red-500/20 text-red-500 hover:bg-red-500/30 text-[10px] font-black tracking-widest uppercase rounded">REJECT</button>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                if (notification.type === 'friend') {
                                                    const friendName = notification.senderId === (gamerUser?.uid || gamerUser?.gamerCode) ? notification.receiverName : notification.senderName;
                                                    return (
                                                        <div key={`real-${notification.id}`} className="flex items-start gap-3 py-[15px] border-b border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 px-2 transition-colors rounded-lg">
                                                            <div className="mt-0.5 shrink-0">
                                                                <IoCheckmarkCircle className="w-5 h-5 text-green-500" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <h4 className="font-['Monda'] font-bold text-sm tracking-wider text-gray-900 dark:text-white leading-tight uppercase truncate">REQUEST ACCEPTED</h4>
                                                                    <span className="font-['Monda'] text-[10px] font-black text-pink-500 whitespace-nowrap ml-2 uppercase tracking-wider shrink-0">JUST NOW</span>
                                                                </div>
                                                                <p className="font-['Monda'] text-xs text-gray-500 dark:text-gray-400 leading-snug uppercase tracking-wide truncate">YOU ARE NOW TEAMMATES WITH {friendName}.</p>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                return null;
                                            })
                                        )}
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>,
                    document.body
                ) : null}

                {typeof window !== 'undefined' ? createPortal(
                    <AnimatePresence>
                        {showLoginModal && (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 z-[999999] flex items-center justify-center bg-black/80 backdrop-blur-md"
                            >
                                <motion.div 
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                            className="bg-white dark:bg-[#1a1a24] p-[15px] rounded-xl border border-gray-200 dark:border-white/10 shadow-2xl max-w-sm w-full font-['Monda'] uppercase"
                        >
                            <div className="border-b border-gray-200 dark:border-white/10 pb-[15px] mb-[15px] text-center">
                                <h3 className="font-['Monda'] text-xl font-black text-gray-900 dark:text-white uppercase tracking-wider">PLAYER AUTHENTICATION</h3>
                            </div>
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 text-center mb-[15px] uppercase">Verify your Player ID to access the competitive arena.</p>
                            
                            <form onSubmit={handleLoginSubmit}>
                                <input 
                                    type="password" 
                                    autoFocus
                                    value={loginPin}
                                    onChange={(e) => setLoginPin(e.target.value.toUpperCase())}
                                    placeholder="GAMER CODE"
                                    className={`w-full font-black text-lg bg-gray-50 dark:bg-black/50 border ${loginError ? 'border-red-500' : 'border-gray-300 dark:border-white/10'} rounded-lg px-4 py-3 focus:outline-none focus:border-pink-500 transition-colors text-gray-900 dark:text-white uppercase text-center`}
                                />
                                {loginError && <p className="text-red-500 font-bold text-xs text-center mt-2">Login Failed.</p>}
                                <div className="flex gap-[15px] mt-[15px]">
                                    <button type="button" onClick={() => { setShowLoginModal(false); setLoginPin(''); setLoginError(false); }} className="flex-1 px-4 py-3 bg-gray-100 dark:bg-white/5 text-gray-900 dark:text-white font-bold text-sm uppercase rounded-lg hover:bg-gray-200 dark:hover:bg-white/10 transition-colors">CANCEL</button>
                                    <button type="submit" disabled={isLoginChecking} className="flex-1 px-4 py-3 bg-pink-500 text-white font-bold text-sm uppercase rounded-lg hover:bg-pink-600 transition-colors disabled:opacity-50 disabled:cursor-wait">
                                        {isLoginChecking ? 'CHECKING...' : 'ENTER'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            ) : null}
        </motion.header>
        </>
    );
}

function MobileToggle({ open, setOpen }) {
    return (
        <button
            className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 dark:border-[#1f1f1f] hover:bg-black/5 dark:hover:bg-white/5 focus:outline-none"
            onClick={() => setOpen(v => !v)}
            aria-label="Toggle navigation menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
        >
            <span className="sr-only">Open menu</span>
            <div className="relative h-4 w-5">
                <span className={`absolute left-0 top-0 block h-0.5 w-5 bg-black dark:bg-white transition-transform duration-300 ${open ? 'translate-y-2 rotate-45' : ''}`} />
                <span className={`absolute left-0 top-1/2 block h-0.5 w-5 -translate-y-1/2 bg-black dark:bg-white transition-opacity duration-300 ${open ? 'opacity-0' : 'opacity-100'}`} />
                <span className={`absolute left-0 bottom-0 block h-0.5 w-5 bg-black dark:bg-white transition-transform duration-300 ${open ? '-translate-y-2 -rotate-45' : ''}`} />
            </div>
        </button>
    );
}

function MobileMenu({ open, setOpen, onLoginClick, currentHash, gamerUser, showNotifications, onToggleNotifications, hasNotifications }) {
    const { t } = useLanguage();
    const { logoutGamer } = useGamerAuth();
    const [isGameswap, setIsGameswap] = useState(window.location.pathname.includes('gameswap'));

    useEffect(() => {
        const handleToggle = (e) => setIsGameswap(e.detail);
        window.addEventListener('gameswapToggle', handleToggle);
        return () => window.removeEventListener('gameswapToggle', handleToggle);
    }, []);

    return (
        <div
            id="mobile-menu"
            className={`md:hidden overflow-hidden transition-[max-height] duration-300 ease-out ${open ? 'max-h-[32rem]' : 'max-h-0'} border-t border-gray-300 dark:border-[#1f1f1f] bg-white dark:bg-[#0a0a0a]`}
            role="dialog"
            aria-modal="false"
        >
            <div className="mx-auto max-w-7xl px-4 py-4">
                <div className="mt-4 flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-3">
                        {!isGameswap && (
                            <AnimatedThemeToggler className="flex flex-1 w-full h-[42px] items-center justify-center rounded-lg bg-[#222] dark:bg-[#1f1f1f] text-gray-400 dark:text-white hover:bg-gray-800 dark:hover:bg-white/10 transition-colors shadow-sm" />
                        )}
                        {!gamerUser ? (
                            <ShimmerButton 
                                onClick={onLoginClick}
                                className="!p-[2px] !h-[42px] flex-1 w-full shadow-sm" 
                                background={isGameswap ? '#0a0a0a' : '#FF9500'} 
                                shimmerColor={isGameswap ? '#A855F7' : '#ffffff'}
                            >
                                <div className="flex z-10 w-full h-full pointer-events-none">
                                    <div className={`flex-1 flex items-center justify-center h-full rounded-md text-xl font-['Iceland'] font-bold transition-all duration-300 uppercase tracking-wider ${!isGameswap ? 'bg-transparent text-white' : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-[0_0_15px_rgba(219,39,119,0.5)]'}`}>
                                        LOGIN
                                    </div>
                                </div>
                            </ShimmerButton>
                        ) : (
                            <button
                                onClick={() => { setOpen(false); window.location.hash = currentHash === '#profile' ? '#' : '#profile'; }}
                                className="flex flex-1 w-full h-[42px] items-center justify-center rounded-lg bg-gray-100 dark:bg-transparent border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-white/10 transition-colors shadow-sm gap-2 text-sm font-['Iceland'] font-bold uppercase tracking-wider"
                            >
                                {currentHash === '#profile' ? (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                                        MAIN LOBBY
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        PROFILE
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    
                    {gamerUser && (
                        <div className="flex flex-row gap-[5px] mt-2 w-full">
                            <ShimmerButton 
                                onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent('openEditProfilePopup')); }}
                                className="!p-[2px] !h-auto flex-1 w-full" background={'#0a0a0a'} shimmerColor={'#ffffff'}
                            >
                                <div className="flex z-10 w-full h-full pointer-events-none">
                                    <div className="flex-1 flex items-center justify-center py-2.5 rounded-md text-[10px] sm:text-xs font-['Iceland'] font-bold transition-all duration-300 uppercase tracking-wider bg-gray-100 dark:bg-transparent text-gray-900 dark:text-white border border-gray-200 dark:border-white/10 leading-none">EDIT</div>
                                </div>
                            </ShimmerButton>
                            <ShimmerButton 
                                onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent('openFriendsPopup')); }}
                                className="!p-[2px] !h-auto flex-1 w-full" background={'#0a0a0a'} shimmerColor={'#ffffff'}
                            >
                                <div className="flex z-10 w-full h-full pointer-events-none">
                                    <div className="flex-1 flex items-center justify-center py-2.5 rounded-md text-[10px] sm:text-xs font-['Iceland'] font-bold transition-all duration-300 uppercase tracking-wider bg-gray-100 dark:bg-transparent text-gray-900 dark:text-white border border-gray-200 dark:border-white/10 leading-none">FRIENDS</div>
                                </div>
                            </ShimmerButton>
                            <ShimmerButton 
                                onClick={() => { if (logoutGamer) { logoutGamer(); window.location.hash = '#'; } setOpen(false); }}
                                className="!p-[2px] !h-auto flex-1 w-full" background={'#FF9500'} shimmerColor={'#ffffff'}
                            >
                                <div className="flex z-10 w-full h-full pointer-events-none">
                                    <div className="flex-1 flex items-center justify-center py-2.5 rounded-md text-[10px] sm:text-xs font-['Iceland'] font-bold transition-all duration-300 uppercase tracking-wider bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-[0_0_15px_rgba(219,39,119,0.5)] leading-none">LOGOUT</div>
                                </div>
                            </ShimmerButton>
                        </div>
                    )}

                    <div className="flex justify-center">
                        {/* {(currentHash !== '#find-teammates' && currentHash !== '#bgmi-accounts' && currentHash !== '#profile' && gamerUser) && <ModeSwitcher isGameswap={isGameswap} />} */}
                    </div>
                </div>
            </div>
        </div>
    );
}
